import type { LDContext } from "launchdarkly-react-client-sdk";
import { ApiProvider } from "@src/store/ApiProvider";

export interface LaunchDarklyContext {
	kind: "user" | "company" | "multi";
	key: string;
}

export interface LaunchDarklyConfig {
	clientSideID: string;
	context: LaunchDarklyContext;
}

export const LD_FLAGS = {
	enableSalesPage: "sf.portal-ui.enable-sales-page",
	enableManualPoCreation: "sf.portal-ui.enable-manual-po-creation",
	ticketmasterPasswordReset: "sf.portal-ui.ticketmaster-password-reset",
	newFeatureIndicator: "sf.portal-ui.new-feature-indicator",
	gametimeListingStatus: "sf.portal-ui.gametime-listing-status",
	estimatedPaymentDate: "sf.portal-ui.enable-estimated-payment-date",
} as const;

const getLaunchDarklyClientId = (): string => {
	const environment = ApiProvider.default.environment;
	const clientId =
		environment === "PROD"
			? process.env.LAUNCH_DARKLY_PRODUCTION_CLIENT_ID || ""
			: process.env.LAUNCH_DARKLY_TEST_CLIENT_ID || "";

	if (!clientId) {
		throw new Error(`LaunchDarkly Client ID is not defined for environment: ${environment}`);
	}

	return clientId;
};

export const DEFAULT_LAUNCH_DARKLY_CONTEXT: LaunchDarklyContext = {
	kind: "user",
	key: "stagefront",
};

export interface LaunchDarklyIdentity {
	userName: string | null | undefined;
	email: string | null | undefined;
	companyName: string | null | undefined;
}

export const buildLaunchDarklyUserContext = ({ userName, email, companyName }: LaunchDarklyIdentity): LDContext => {
	if (!userName) return DEFAULT_LAUNCH_DARKLY_CONTEXT;

	const user = { key: userName, name: userName, ...(email ? { email } : {}) };
	if (!companyName) return { kind: "user", ...user };

	return {
		kind: "multi",
		user,
		company: { key: companyName, name: companyName },
	};
};

export const getLaunchDarklyConfig = (): LaunchDarklyConfig => {
	const clientSideID = getLaunchDarklyClientId();

	return {
		clientSideID,
		context: DEFAULT_LAUNCH_DARKLY_CONTEXT,
	};
};
